/**
* @summary - Component that Provides Scheduled Maintenance Calendar
* @author Yuvasri S
* @since 2024-07-24
*/
import React, { useEffect, useState } from 'react';
import { Grid, FormControl, FormGroup, FormControlLabel, Checkbox, Stack, Box, Drawer, Paper, Typography, TextField } from '@mui/material';
import { DefaultCard } from '../../assets/styled-components/card';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../assets/styled-components/custom-big-calender.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import AccessTimeFilledOutlinedIcon from '@mui/icons-material/AccessTimeFilledOutlined';
import { useTheme } from '@mui/material';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import axios from "axios";
import { getAuthToken, getcompanyId, getFacilityId } from "../../utility/tokenUtility";
import { MasterGraph, FemsGraph, BemsGraph } from "../../utility/apiURLS";
import dayjs from 'dayjs';
import Badge from '@mui/material/Badge';
import 'dayjs/locale/en';
import PageTitle from '../commoncomponents/PageTitle';
import CloseButton from "../commoncomponents/CloseButton";


dayjs.locale('en');

const Scheduled = () => {
    const theme = useTheme();
    const [service, setService] = useState(null);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [workgroups, setWorkgroups] = useState([]);
    const [selectedWorkgroups, setSelectedWorkgroups] = useState([]);
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [workgroupColors, setWorkgroupColors] = useState({});
    const [allChecked, setAllChecked] = useState(true); // State for "All" checkbox
    const [loading, setLoading] = useState(true); // State for loading
    const [view, setView] = useState('month');
    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const companyId = getcompanyId('companyId');
    const facilityId = getFacilityId('facilityId');

    // Function to generate a random color
    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };
    const generateWorkgroupColors = (workgroups) => {
        const colors = {};
        workgroups.forEach(workgroup => {
            colors[workgroup.label] = getRandomColor();
        });
        setWorkgroupColors(colors);
        setSelectedWorkgroups(workgroups); // Select all workgroups by default
    };


    const fetchServiceOptions = async () => {
        try {
            const response = await axios.get(`${MasterGraph}/api/masters/GetMasters/${companyId}/${facilityId}`, config);
            const serviceData = response.data.masservicedetails;
            const options = serviceData.map(service => ({
                value: service.servicedesc,
                label: service.service,
                id: service.id
            }));
            setServiceOptions(options);
            setService(options[0]);
            handleServiceChange(options[0]);
        } catch (error) {
            console.error('Error fetching service options:', error);
        }
    };



    // Use the updated function in fetchFemsWorkgroups and fetchBemsWorkgroups
    const fetchFemsWorkgroups = async () => {
        try {
            const femsResponse = await axios.get(`${FemsGraph}/api/WorkGroupDetails/GetWorkGroupDetailsList/${facilityId}?page=1&size=50`, config);
            const femsWorkgroups = femsResponse.data.items.map(workgroup => ({
                label: workgroup.wgcode,
                id: workgroup.wgId
            }));
            setWorkgroups(femsWorkgroups);
            generateWorkgroupColors(femsWorkgroups);
            fetchAllScheduledWorkOrders(selectedDate.format('YYYY-MM'), 'fems', femsWorkgroups);
        } catch (error) {
            console.error('Error fetching FEMs workgroups:', error);
        }
    };

    const fetchBemsWorkgroups = async () => {
        try {
            const bemsResponse = await axios.get(`${BemsGraph}/api/WorkGroupDetails/GetWorkGroupDetailsList/${facilityId}?page=1&size=50`, config);
            const bemsWorkgroups = bemsResponse.data.items.map(workgroup => ({
                label: workgroup.wgcode,
                id: workgroup.wgId
            }));
            setWorkgroups(bemsWorkgroups);
            generateWorkgroupColors(bemsWorkgroups);
            fetchAllScheduledWorkOrders(selectedDate.format('YYYY-MM'), 'bems', bemsWorkgroups);
        } catch (error) {
            console.error('Error fetching BEMs workgroups:', error);
        }
    };



    const fetchAllScheduledWorkOrders = async (monthYear, serviceType, workgroups) => {
        try {
            setLoading(true); // Set loading to true before fetching data
            const serviceUrl = serviceType === 'fems' ? FemsGraph : BemsGraph;
            const response = await axios.get(`${serviceUrl}/api/GraphMaintenanceCalendar/GetAllMainCal${serviceType === 'fems' ? 'Fem' : 'Bem'}ScheduleWorkorder/${monthYear}/${facilityId}?size=100000`, config);

            const workOrders = response.data.map(order => ({
                start: new Date(order.targetdate),
                end: new Date(order.targetdate),
                title: `${order.taskcode} - ${order.taskdesc}`,
                taskdesc: order.taskdesc,
                workorderno: order.workorderno,
                data: {
                    type: order.wgcode,
                    name: order.requestdetails,
                    bgColor: workgroupColors[order.wgcode] || '#2D85C5'
                }
            }));

            const filteredWorkOrders = workOrders.filter(order =>
                workgroups.some(workgroup => workgroup.label === order.data.type)
            );

            setEvents(filteredWorkOrders);
            setFilteredEvents(filteredWorkOrders);
        } catch (error) {
            console.error('Error fetching all scheduled work orders:', error);
        } finally {
            setLoading(false); // Set loading to false after fetching data
        }
    };

    const fetchScheduledWorkOrders = async (service, monthYear, workgroupIds) => {
        if (!service || !workgroupIds.length) return;

        const serviceUrl = service.label.toLowerCase() === 'fems' ? `${FemsGraph}` : `${BemsGraph}`;
        try {
            setLoading(true); // Set loading to true before fetching data
            const workOrders = [];
            for (const workgroupId of workgroupIds) {
                const response = await axios.get(`${serviceUrl}/api/GraphMaintenanceCalendar/GetAllworkgroupMainCalFemScheduleWorkorder/${workgroupId}/${monthYear}`, config);
                response.data.forEach(order => {
                    workOrders.push({
                        start: new Date(order.targetdate),
                        end: new Date(order.targetdate),
                        title: `${order.taskcode} - ${order.taskdesc}`,
                        taskdesc: order.taskdesc,
                        workorderno: order.workorderno,
                        data: {
                            type: order.wgcode,
                            name: order.requestdetails,
                            bgColor: workgroupColors[order.wgcode] || '#2D85C5'
                        }
                    });
                });
            }
            setEvents(workOrders);
            setFilteredEvents(workOrders);
        } catch (error) {
            console.error('Error fetching scheduled work orders:', error);
        } finally {
            setLoading(false); // Set loading to false after fetching data
        }
    };

    const handleServiceChange = (newValue) => {
        setService(newValue);
        setEvents([]);
        setFilteredEvents([]);
        if (newValue.label.toLowerCase() === 'fems') {
            fetchFemsWorkgroups();
        } else if (newValue.label.toLowerCase() === 'bems') {
            fetchBemsWorkgroups();
        } else {
            setWorkgroups([]);
        }
        setAllChecked(true); // Ensure "All" checkbox is checked
    };
    useEffect(() => {
        fetchServiceOptions();
    }, []);


    useEffect(() => {
        if (service) {
            if (service.label.toLowerCase() === 'fems') {
                fetchFemsWorkgroups();
            } else if (service.label.toLowerCase() === 'bems') {
                fetchBemsWorkgroups();
            }
        }
    }, [service]);

    useEffect(() => {
        if (workgroups.length > 0 && selectedDate) {
            if (service) {
                fetchAllScheduledWorkOrders(selectedDate.format('YYYY-MM'), service.label.toLowerCase(), workgroups);
            }
        }
    }, [workgroups, selectedDate, service, workgroupColors]);

    useEffect(() => {
        // Optional: Ensure initial view is correctly set
        setView('month'); // or 'week', 'day' based on initial requirement
    }, []);


    // Handle 'All' checkbox change
    const handleAllCheckboxChange = (event) => {
        const { checked } = event.target;
        setAllChecked(checked);
        if (checked) {
            setSelectedWorkgroups(workgroups);
            setFilteredEvents(events);
        } else {
            setSelectedWorkgroups([]);
            setFilteredEvents([]);
        }
    };

    // Handle individual workgroup checkbox change
    const handleCheckboxChange = (event) => {
        const { name, checked, value } = event.target;
        setSelectedWorkgroups(prevState => {
            const newState = checked
                ? [...prevState, { label: name, id: value }]
                : prevState.filter(workgroup => workgroup.label !== name);

            const filteredEvents = events.filter(event =>
                newState.some(workgroup => workgroup.label === event.data.type)
            );
            setFilteredEvents(filteredEvents);

            if (newState.length === workgroups.length) {
                setAllChecked(true);
            } else {
                setAllChecked(false);
            }

            return newState;
        });
    };



    const handleDateChange = (date) => {
        setSelectedDate(dayjs(date));  // Update to ensure `selectedDate` is always a dayjs object
    };

    const handleViewChange = (newView) => {
        console.log('View changing to:', newView);
        setView(newView); // Update view state
    };

    const handleDateClick = (event) => {
        const clickedDateEvents = filteredEvents.filter(ev => moment(ev.start).isSame(event.start, 'day'));
        setSelectedEvents(clickedDateEvents);
        setDrawerOpen(true);
    };

    const localizer = momentLocalizer(moment);

    const CustomEventComponent = ({ event }) => {
        const multipleEvents = filteredEvents.filter(ev => moment(ev.start).isSame(event.start, 'day')).length > 1;

        return (
            <Box onClick={() => handleDateClick(event)} style={{ cursor: 'pointer' }}>
                {multipleEvents ? (
                    <Badge>
                        <Box className="event-content" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{ flexWrap: 'wrap' }}>
                                <Typography
                                    component={'span'}
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        display: 'inline-block',
                                        borderRadius: '50%',
                                        backgroundColor: event.data.bgColor
                                    }}
                                ></Typography>
                                <Typography sx={{ color: 'grey.700', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {event.data.type}
                                </Typography>
                            </Stack>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {event.taskdesc}
                            </Typography>
                        </Box>
                    </Badge>
                ) : (
                    <Box className="event-content" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{ flexWrap: 'wrap' }}>
                            <Typography
                                component={'span'}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    display: 'inline-block',
                                    borderRadius: '50%',
                                    backgroundColor: event.data.bgColor
                                }}
                            ></Typography>
                            <Typography
                                sx={{
                                    width: 'auto',
                                    height: 'auto',
                                    color: 'grey.700',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {event.data.type}
                            </Typography>
                        </Stack>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                width: 'auto',
                                height: 'auto',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {event.taskdesc}
                        </Typography>
                    </Box>
                )}
            </Box>

        );
    };

    const LabelText = ({ labelName, bgColor }) => (
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Typography width={8} height={8} sx={{ backgroundColor: bgColor, borderRadius: 50, }} />
            <Typography variant={'caption'} fontWeight={500} color={'text.grey'}>{labelName}</Typography>
        </Stack>
    );

    // Function to apply custom styles to specific days
    const dayPropGetter = (date) => {
        // Check if the day is Sunday
        if (date.getDay() === 0) {
            return {
                className: 'holiday'
            };
        }
        return {};
    };

    const formats = {
        timeGutterFormat: (date, culture, localizer) =>
            localizer.format(date, 'HH:mm', culture),
        eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
    };
    const handleEventClick = (event) => {
        const selectedEventData = events.filter(evt => evt.start.getTime() === event.start.getTime());
        setSelectedEvents(selectedEventData);
        setDrawerOpen(true);
    };


    return (

        <DefaultCard>
            <Grid container spacing={2} mb={2} sx={{ overflow: "scroll" }}>
                <Grid item xs={12} sm={2}>
                    <CustomSelectInput
                        options={serviceOptions}
                        value={service}
                        label="Service"
                        onChange={handleServiceChange}
                    />
                </Grid>
                <Grid item xs={12} sm={8} sx={{ overflow: "scroll" }}>
                    <FormControl component="fieldset">
                        <FormGroup>
                            <Stack direction="row" flexWrap="wrap">
                                <div>
                                    {/* "All" Checkbox */}
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={allChecked}
                                                onChange={handleAllCheckboxChange}
                                                name="all"
                                            //color="primary"
                                            />
                                        }
                                        label="All"
                                    />

                                    {/* Individual Workgroup Checkboxes */}
                                    {workgroups.map(workgroup => (
                                        <FormControlLabel
                                            key={workgroup.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedWorkgroups.some(selected => selected.label === workgroup.label)}
                                                    onChange={handleCheckboxChange}
                                                    name={workgroup.label}
                                                    value={workgroup.id}
                                                //color="primary"
                                                //style={{ color: workgroupColors[workgroup.label] }}
                                                />
                                            }
                                            label={
                                                <span>
                                                    <span
                                                        style={{
                                                            display: 'inline-block',
                                                            width: '10px',
                                                            height: '10px',
                                                            backgroundColor: workgroupColors[workgroup.label],
                                                            marginRight: '8px',
                                                            borderRadius: '50%'
                                                        }}
                                                    />
                                                    {workgroup.label}
                                                </span>
                                            }
                                        />
                                    ))}
                                </div>

                            </Stack>
                        </FormGroup>
                    </FormControl>

                </Grid>


                <Grid item xs={12} sm={2} sx={{ overflow: "scroll" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            views={['year', 'month']}
                            value={selectedDate}
                            onChange={handleDateChange}
                            openTo="month"
                            format="MMMM YYYY"
                            label="Select Month and Year"
                            sx={{
                                '& .MuiInputBase-root': {
                                    width: '170px',
                                    height: '38px',
                                },
                                '& .MuiInputLabel-root': {
                                    mt: '-7px',
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
            </Grid>

            <Grid item xs={12} sx={{ overflow: "scroll" }}>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                        <Typography variant="h6">Loading...</Typography>
                    </Box>
                ) : (
                    <Paper className="calender-paper" >
                        <Calendar
                            localizer={localizer}
                            events={filteredEvents}
                            startAccessor="start"
                            endAccessor="end"
                            titleAccessor="title"
                            formats={formats}
                            onSelectEvent={handleEventClick}
                            style={{ height: 550 }} // Keep height here
                            eventPropGetter={() => ({
                                style: {
                                    backgroundColor: 'transparent',
                                    color: 'black',
                                    borderRadius: '8px',
                                    border: 'none',
                                    width: '100%',
                                }
                            })}
                            components={{ event: CustomEventComponent }}
                            selectable={true}
                            popup={true}
                            messages={{
                                moreEvents: '+{{count}} more' // Custom message for more events
                            }}
                            date={selectedDate.toDate()} // Set the date prop to selectedDate
                            defaultView='month'
                            view={view} // Bind to state
                            onView={handleViewChange} // Handle view change
                            views={['day', 'month', 'week']}
                            onNavigate={handleDateChange}
                            className="maintenance-calender-theme"
                            dayPropGetter={dayPropGetter}
                        />

                        {/* Apply styles dynamically to .rbc-time-view */}
                        <style>
                            {`
                 .rbc-time-view {
                overflow: scroll !important;
                 max-height: 550px;
        }
    `}
                        </style>

                    </Paper>
                )}
            </Grid>


            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{ overflow: "scroll" }} // Apply overflow properly
            >

                <Box sx={{ width: { xs: 'auto', sm: 300 }, overflow: "scroll" }}>

                    <Typography p={2} color={'primary'} variant="h6" component="div">
                        Workorder List
                    </Typography>
                    {selectedEvents.map((event, index) => (
                        <Paper
                            key={index}
                            sx={{
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                borderColor: 'border.main',
                                p: 1,
                                mb: { xs: 1, md: 1.5 }
                            }}
                        >
                            <Box>
                                <Stack mb={1} direction={'row'} spacing={1.5} useFlexGap alignItems={'center'} overflow={'scroll'}>
                                    <Typography
                                        component={'span'}
                                        width={12}
                                        height={12}
                                        display={'inline-block'}
                                        borderRadius={'50%'}
                                        backgroundColor={event.data.bgColor}
                                    ></Typography>
                                    <Typography fontSize={14} sx={{ color: 'grey.700' }}>{event.data.type}</Typography>
                                </Stack>
                                <Typography mb={1} color={'text.primary'} fontWeight={500}>{event.workorderno}</Typography>
                                <Typography mb={1} color={'text.primary'} fontWeight="bold">{event.title}</Typography>
                                <Stack mb={0.75} direction={'row'} spacing={1.5} alignItems={'center'}>
                                    <Person4OutlinedIcon />
                                    <Typography color={'text.grey'} fontSize={14}>{event.data.name}</Typography>
                                </Stack>
                                {/* <Stack mb={1} direction={'row'} spacing={1.5} alignItems={'center'}>
                                    <AccessTimeFilledOutlinedIcon />
                                    <Typography color={'text.grey'} fontSize={14}>
                                        {moment(event.start).format('h:mma')} - {moment(event.end).format('h:mma')}
                                    </Typography>
                                </Stack> */}
                            </Box>
                        </Paper>
                    ))}
                </Box>
            </Drawer>
            <CloseButton />

        </DefaultCard>
    );
};

export default Scheduled;
